/** Org plan definition helpers — product runtime reads planDefinition only. */

function planIsActive(state) {
  const status = state?.planDefinition?.status;
  return status === 'active' || status === 'approved';
}

function activeDef(state) {
  if (!planIsActive(state)) return null;
  return state?.planDefinition || null;
}

export function orgTemplateId(state) {
  return String(activeDef(state)?.provenance?.templateId || state?.planDefinition?.provenance?.templateId || '');
}

export function planNeedsSetup(state) {
  const def = state?.planDefinition;
  const status = def?.status;
  if (status === 'active' || status === 'approved') return false;
  if (status === 'needs_setup' || status === 'draft' || status === 'pending_review') return true;
  const epics = state?.plan?.epics || [];
  return epics.length === 0;
}

export function orgEpics(state) {
  const def = state?.planDefinition;
  if (planIsActive(state) && Array.isArray(def?.epics) && def.epics.length) {
    return def.epics;
  }
  if (planIsActive(state)) {
    const runtime = state?.plan?.epics || [];
    if (runtime.length) return runtime;
  }
  return [];
}

export function orgEpicClass(state, epicId) {
  const fromDef = orgEpics(state).find((e) => e.id === epicId);
  return fromDef?.epicClass || undefined;
}

export function orgEpicStrategyStatus(state, epicId) {
  const fromDef = orgEpics(state).find((e) => e.id === epicId);
  return fromDef?.strategyStatus || undefined;
}

export function orgEpicMarket(state, epicId) {
  const fromDef = orgEpics(state).find((e) => e.id === epicId);
  return fromDef?.market || undefined;
}

export function orgGateOutcomes(state) {
  const def = state?.planDefinition;
  if (planIsActive(state) && Array.isArray(def?.gateOutcomes) && def.gateOutcomes.length) {
    return def.gateOutcomes;
  }
  return [];
}

export function orgOutcomesForEpic(state, epicId, gateDay) {
  return orgGateOutcomes(state).filter(
    (o) => o.gateDay === Number(gateDay) && (o.epicIds || []).includes(epicId),
  );
}

export function orgTimeTargets(state) {
  const def = state?.planDefinition;
  if (planIsActive(state) && Array.isArray(def?.timeTargets) && def.timeTargets.length) {
    return def.timeTargets;
  }
  return [];
}

export function orgMarkets(state) {
  const def = state?.planDefinition;
  if (planIsActive(state) && Array.isArray(def?.markets) && def.markets.length) {
    return def.markets;
  }
  return [];
}

/** Market labels used for commercial funnel account inclusion. */
export function orgMarketLabels(state) {
  return orgMarkets(state)
    .map((m) => String(m.label || '').trim())
    .filter(Boolean);
}

export function orgWorkstreams(state) {
  const def = state?.planDefinition;
  if (planIsActive(state) && Array.isArray(def?.workstreams) && def.workstreams.length) {
    return def.workstreams;
  }
  return [];
}

export function orgPipelines(state) {
  const def = state?.planDefinition;
  if (planIsActive(state) && Array.isArray(def?.pipelines) && def.pipelines.length) {
    return def.pipelines;
  }
  return [];
}

export function orgPipelineByKey(state, key) {
  if (!key) return null;
  return orgPipelines(state).find((p) => p.key === key) || null;
}

export function orgPrimaryPipeline(state) {
  const pipelines = orgPipelines(state);
  const withStages = pipelines.find((p) => Array.isArray(p.stages) && p.stages.length);
  if (withStages) return withStages;
  const tabbed = pipelines.find((p) => p.pipelineTab !== false);
  return tabbed || pipelines[0] || null;
}

/**
 * Opportunity stage catalog from plan pipelines (config-native).
 * Empty when no active plan — UI should show Plan Setup, not Blocks defaults.
 */
export function orgOpportunityStages(state) {
  const primary = orgPrimaryPipeline(state);
  if (Array.isArray(primary?.stages) && primary.stages.length) {
    return [...primary.stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  const any = orgPipelines(state).find((p) => Array.isArray(p.stages) && p.stages.length);
  if (any?.stages?.length) {
    return [...any.stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  return [];
}

export function orgOpportunityStageLabels(state) {
  return orgOpportunityStages(state).map((s) => s.label).filter(Boolean);
}

/** Rank map keyed by stage label (and id for convenience). */
export function orgOpportunityStageRank(state) {
  const stages = orgOpportunityStages(state);
  const map = {};
  stages.forEach((s, i) => {
    if (s.label) map[s.label] = i;
    if (s.id) map[s.id] = i;
  });
  return map;
}

export function orgQualifyStageLabel(state) {
  const stages = orgOpportunityStages(state);
  const q = stages.find((s) => s.qualifies);
  return q?.label || stages.find((s) => /qualified/i.test(s.label || ''))?.label || 'Qualified';
}

export function orgTerminalStageLabel(state) {
  const stages = orgOpportunityStages(state);
  const t = stages.find((s) => s.terminal);
  return t?.label || 'Won / Lost / Parked';
}

export function orgCommercialScope(state) {
  const def = activeDef(state);
  return (
    def?.commercialScope || {
      mode: 'all',
      includeTierAAlways: false,
    }
  );
}

export function orgMarketAliases(state) {
  const def = activeDef(state);
  return def?.marketAliases && typeof def.marketAliases === 'object' ? def.marketAliases : {};
}

export function orgFunnelMetrics(state) {
  const def = activeDef(state);
  if (Array.isArray(def?.funnelMetrics) && def.funnelMetrics.length) return def.funnelMetrics;
  return [];
}

export function orgAgingMetrics(state) {
  const def = activeDef(state);
  if (Array.isArray(def?.agingMetrics) && def.agingMetrics.length) return def.agingMetrics;
  return [];
}

export function orgPrimaryDeal(state) {
  const def = activeDef(state);
  if (def?.primaryDeal && typeof def.primaryDeal === 'object') return def.primaryDeal;
  return { enabled: false, label: 'Primary deal', candidateKey: 'd001Candidate', primaryKey: 'primaryD001' };
}

export function orgPrimaryDealEnabled(state) {
  return !!orgPrimaryDeal(state)?.enabled;
}

export function orgCoachHints(state) {
  const def = activeDef(state);
  return def?.coachHints || null;
}

export function orgEmptyCopy(state) {
  const def = activeDef(state);
  return def?.emptyCopy && typeof def.emptyCopy === 'object' ? def.emptyCopy : {};
}

/**
 * Pipeline tabs: prefer named pipelines with explicit panel / deepWorkspace.
 * No saudi/dubai key inference — templates must set panel explicitly.
 */
export function orgPipelineTabs(state) {
  const pipelines = orgPipelines(state).filter((p) => p.pipelineTab !== false);
  if (pipelines.length) {
    return pipelines.map((p) => ({
      key: p.key,
      label: p.name || p.label || p.key,
      panel: p.panel || 'commercial',
      kind: p.kind || 'commercial',
      deepWorkspace: !!p.deepWorkspace,
      pipeline: p,
    }));
  }
  // Workstream fallback: commercial only (no market-named panel inference).
  return orgWorkstreams(state)
    .filter((w) => w.pipelineTab)
    .map((w) => ({
      key: w.key,
      label: w.label,
      panel: 'commercial',
      kind: 'commercial',
      deepWorkspace: false,
      pipeline: {
        key: w.key,
        name: w.label,
        kind: 'commercial',
        workstreamKey: w.key,
        marketKey: w.key,
        defaultMarket: w.label,
        pipelineTab: true,
        panel: 'commercial',
        deepWorkspace: false,
      },
    }));
}

/** First pipeline with deepWorkspace or primaryDeal.enabled. */
export function orgDeepWorkspacePipeline(state) {
  if (!orgPrimaryDealEnabled(state)) {
    return orgPipelines(state).find((p) => p.deepWorkspace) || null;
  }
  return (
    orgPipelines(state).find((p) => p.deepWorkspace || p.panel === 'primary_deal') ||
    orgPrimaryPipeline(state)
  );
}

export function orgLockedStrategy(state) {
  const def = state?.planDefinition;
  if (planIsActive(state) && def?.lockedStrategy && typeof def.lockedStrategy === 'object') {
    return def.lockedStrategy;
  }
  return null;
}

export function orgStrategyApprovedAt(state) {
  const fromSettings = state?.settings?.strategyApprovedAt;
  if (fromSettings) return String(fromSettings).slice(0, 10);
  const fromDef = state?.planDefinition?.approvedAt;
  if (planIsActive(state) && fromDef) return String(fromDef).slice(0, 10);
  return '';
}

export function orgAskQuestions(state) {
  const def = activeDef(state);
  if (Array.isArray(def?.askQuestions) && def.askQuestions.length) return def.askQuestions;
  return null;
}

export function orgSegmentOptions(state) {
  const def = activeDef(state);
  if (Array.isArray(def?.segmentOptions) && def.segmentOptions.length) return def.segmentOptions;
  return null;
}

export function orgWhyUsLabel(state) {
  const def = activeDef(state);
  return def?.uiCopy?.whyUsLabel || null;
}

export function orgFunnelTargetsTitle(state) {
  const def = activeDef(state);
  return def?.uiCopy?.funnelTargetsTitle || null;
}

export function orgDeepWorkspaceNavLabel(state) {
  const def = activeDef(state);
  return def?.uiCopy?.deepWorkspaceNavLabel || orgPrimaryDeal(state)?.label || null;
}

export function orgCsvExportLabels(state) {
  const def = activeDef(state);
  return def?.uiCopy?.csvExportLabels && typeof def.uiCopy.csvExportLabels === 'object'
    ? def.uiCopy.csvExportLabels
    : {};
}

export function orgFunnelMetricLabels(state) {
  const def = activeDef(state);
  const fromMetrics = {};
  for (const m of orgFunnelMetrics(state)) {
    if (m.key && m.label) fromMetrics[m.key] = m.label;
  }
  const legacy = def?.funnelMetricLabels && typeof def.funnelMetricLabels === 'object' ? def.funnelMetricLabels : {};
  return { ...legacy, ...fromMetrics };
}

export function orgReviewConcentrate(state) {
  const def = activeDef(state);
  return def?.reviewConcentrate || null;
}

export function orgEvidenceLadder(state) {
  const def = activeDef(state);
  if (Array.isArray(def?.evidenceLadder) && def.evidenceLadder.length) return def.evidenceLadder;
  return null;
}

export function orgBridgeMandate(state) {
  const def = activeDef(state);
  return def?.bridgeMandate || null;
}

export function buildJiraUrlFromEpic(baseUrl, epic) {
  if (epic?.jiraIssueKey && baseUrl) {
    const base = String(baseUrl).replace(/\/+$/, '');
    return `${base}/browse/${String(epic.jiraIssueKey).trim()}`;
  }
  return null;
}
